import { createEntityAdapter, EntityAdapter, EntityState, Update } from '@ngrx/entity';
import { SetHotelRoomResultsPayload } from '@shared/models/hotel-search-payload.model';
import { RoomService } from '@shared/utils/room.service';
import { clearLocalSelectedRooms, getHash } from '@shared/utils/travel-utils';

import {
  BannerData,
  HotelRoom,
  HotelRoomPriceOrdering,
  HotelRoomWithKey,
  MealBasis,
} from '@shared/models/hotel-search-response.model';
import { ROOMS_LOCAL_STORAGE_KEY } from 'app.config';
import * as fromActions from '../actions/hotel-room.actions';
import * as fromHotelSearchActions from '../actions/hotel-search.actions';

export interface HotelRoomResult {
  rooms: HotelRoomWithKey[];
  filteredRooms: HotelRoomWithKey[];
  sequenceNumber: number;
  numberOfGuests: number;
  selectedRoom: HotelRoom;
  bannerData: BannerData;
  defaultPriceOrder: HotelRoomPriceOrdering;
  selectedPriceOrder: HotelRoomPriceOrdering;
  roomLimit: number;
}

export interface HotelRoomResultsState extends EntityState<HotelRoomResult> {
  loading: boolean;
}

export const hotelRoomResultsAdapter: EntityAdapter<HotelRoomResult> = createEntityAdapter<HotelRoomResult>({
  selectId: (roomResult) => roomResult.sequenceNumber,
});

export const initialState = hotelRoomResultsAdapter.getInitialState({
  loading: true,
});

export function reducer(
  state = initialState,
  action: fromActions.HotelRoomActions | fromHotelSearchActions.HotelSearchActions
): HotelRoomResultsState {
  switch (action.type) {
    case fromActions.SET_HOTEL_ROOM_RESULTS_SUCCESS: {
      let newState = {} as EntityState<HotelRoomResult>;

      const localRoomResults = JSON.parse(
        sessionStorage.getItem(ROOMS_LOCAL_STORAGE_KEY + '_' + getHash(action.payload.hotelId))
      );

      if (localRoomResults) {
        newState = localRoomResults;
      } else {
        const hotelRoomResults = mapRoomsToRoomResults(action.payload);
        newState = hotelRoomResultsAdapter.setMany(hotelRoomResults, { ...state });
        sessionStorage.setItem(
          ROOMS_LOCAL_STORAGE_KEY + '_' + getHash(action.payload.hotelId),
          JSON.stringify(newState)
        );
      }

      return { ...newState, loading: false };
    }

    case fromActions.CLEAR_HOTEL_ROOM_RESULTS: {
      clearLocalSelectedRooms();
      return hotelRoomResultsAdapter.removeAll({ ...state, loading: true });
    }

    case fromActions.RESET_HOTEL_ROOM_RESULTS: {
      clearLocalSelectedRooms();
      return hotelRoomResultsAdapter.removeAll({ ...state, loading: false });
    }

    case fromActions.SELECT_HOTEL_ROOM_V2: {
      const updates: Update<HotelRoomResult>[] = [];
      const resultUpdate: Update<HotelRoomResult> = {
        id: action.payload.room.seq,
        changes: {
          selectedRoom: action.payload.room,
          bannerData: action.payload.banner,
        },
      };

      updates.push(resultUpdate);

      if (action.payload.room.seq === 1) {
        state.ids.forEach((i) => {
          if (i === 1) {
            return;
          }

          const update: Update<HotelRoomResult> = {
            id: i as number,
            changes: {},
          };

          const otherSelectedRoom = state.entities[i].selectedRoom;
          const currentRoom = action.payload.room;

          if (
            otherSelectedRoom &&
            (otherSelectedRoom.supplier !== currentRoom.supplier ||
              otherSelectedRoom.product_code !== currentRoom.product_code)
          ) {
            update.changes.selectedRoom = null;
          }

          if (
            action.payload.isSameRoomSupplier &&
            (otherSelectedRoom?.room_type !== currentRoom.room_type ||
              otherSelectedRoom?.meal_basis !== currentRoom.meal_basis ||
              otherSelectedRoom?.is_non_refundable !== currentRoom.is_non_refundable)
          ) {
            update.changes.selectedRoom = null;
          }

          update.changes.filteredRooms = state.entities[i].rooms
            .slice(0)
            .filter((room: HotelRoomWithKey) => {
              if (action.payload.isSameRoomSupplier) {
                return (
                  room.key.supplier === currentRoom.supplier &&
                  room.key.productCode === currentRoom.product_code &&
                  room.key.roomType === currentRoom.room_type &&
                  room.key.nonRefundable === currentRoom.is_non_refundable
                );
              }
              return room.key.supplier === currentRoom.supplier && room.key.productCode === currentRoom.product_code;
            })
            .map((room) => ({
              ...room,
              meals: action.payload.isSameRoomSupplier
                ? room.meals.filter((meal: MealBasis) => meal.id === currentRoom.meal_basis)
                : room.meals,
            }));

          update.changes.filteredRooms = update.changes.filteredRooms.filter(
            (room: HotelRoomWithKey) => !!room.meals.length
          );

          updates.push(update);
        });
      }
      const newState = hotelRoomResultsAdapter.updateMany(updates, state);
      sessionStorage.setItem(ROOMS_LOCAL_STORAGE_KEY + '_' + getHash(action.payload.hotelId), JSON.stringify(newState));
      return newState;
    }

    case fromActions.CHANGE_ROOM_LIMIT: {
      const update: Update<HotelRoomResult> = {
        id: action.payload.seq,
        changes: {
          roomLimit: action.payload.roomLimit,
        },
      };
      const newState = hotelRoomResultsAdapter.updateOne(update, state);
      sessionStorage.setItem(ROOMS_LOCAL_STORAGE_KEY + '_' + getHash(action.payload.hotelId), JSON.stringify(newState));
      return newState;
    }

    case fromActions.SORT_HOTEL_ROOM_RESULTS: {
      const updates: Update<HotelRoomResult>[] = [];
      state.ids.forEach((i) => {
        const sortFunction = (a: HotelRoomWithKey, b: HotelRoomWithKey) =>
          sortRoomsByPrice(a, b, action.payload.selectedPriceOrder, state.entities[i].defaultPriceOrder);

        const filteredRoomsCopy = state.entities[i].filteredRooms.slice(0);
        const sortedFilteredRooms = filteredRoomsCopy.sort(sortFunction);

        const roomsCopy = state.entities[i].rooms.slice(0);
        const sortedRooms = roomsCopy.sort(sortFunction);

        const update: Update<HotelRoomResult> = {
          id: i as number,
          changes: {
            rooms: sortedRooms,
            filteredRooms: sortedFilteredRooms,
            selectedPriceOrder: action.payload.selectedPriceOrder,
          },
        };
        updates.push(update);
      });
      const newState = hotelRoomResultsAdapter.updateMany(updates, state);
      sessionStorage.setItem(ROOMS_LOCAL_STORAGE_KEY + '_' + getHash(action.payload.hotelId), JSON.stringify(newState));
      return newState;
    }

    default:
      break;
  }
  return state;
}

export const mapRoomsToRoomResults = (payload: SetHotelRoomResultsPayload) => {
  const hotelRoomResults: HotelRoomResult[] = [];
  const numberOfRequestedRooms = payload.roomRequests.length;

  for (let i = 0; i < numberOfRequestedRooms; i++) {
    const sequenceNumber = i + 1;
    const roomsBySequenceNumber = payload.rooms.filter((room: HotelRoom) => room.seq === sequenceNumber);
    const roomRequest = payload.roomRequests[i];
    const numberOfGuests = roomRequest.num_adults + roomRequest.num_infants + roomRequest.children_ages.length;

    const hotelRoomsWithKey = RoomService.mapRoomsToHotelRoomsWithKey(
      roomsBySequenceNumber,
      payload.productGroups,
      payload.isPricePerRoomPerNight,
      payload.productGroupsForBanner,
      payload.roomRequests,
      sequenceNumber
    );
    hotelRoomsWithKey.sort((a: HotelRoomWithKey, b: HotelRoomWithKey) =>
      sortRoomsByPrice(a, b, payload.selectedPriceOrder, payload.defaultPriceOrder)
    );

    const hotelRoomResult: HotelRoomResult = {
      rooms: hotelRoomsWithKey,
      filteredRooms: hotelRoomsWithKey,
      numberOfGuests,
      selectedRoom: null,
      bannerData: null,
      sequenceNumber,
      selectedPriceOrder: payload.selectedPriceOrder || payload.defaultPriceOrder,
      defaultPriceOrder: payload.defaultPriceOrder,
      roomLimit: 10,
    };
    hotelRoomResults.push(hotelRoomResult);
  }
  return hotelRoomResults;
};

export const sortRoomsByPrice = (
  a: HotelRoomWithKey,
  b: HotelRoomWithKey,
  order: HotelRoomPriceOrdering,
  defaultOrder: HotelRoomPriceOrdering
) => {
  const lowestRoomPrice1 = +a.meals[0].price.amount;
  const lowestRoomPrice2 = +b.meals[0].price.amount;
  switch (order) {
    case HotelRoomPriceOrdering.ASCENDING:
      return lowestRoomPrice1 - lowestRoomPrice2;
    case HotelRoomPriceOrdering.DESCENDING:
      return lowestRoomPrice2 - lowestRoomPrice1;
    case HotelRoomPriceOrdering.RECOMMENDED: {
      const groupPriorityOrderDiff = a.key.roomGroup.priority - b.key.roomGroup.priority;
      if (groupPriorityOrderDiff === 0) {
        if (defaultOrder === HotelRoomPriceOrdering.ASCENDING) {
          return lowestRoomPrice1 - lowestRoomPrice2;
        }
        return lowestRoomPrice2 - lowestRoomPrice1;
      }
      return groupPriorityOrderDiff;
    }
    default:
      return 0;
  }
};
